/**
 * Daily opens/clicks for an email campaign or automation, drawn as bars, a line
 * or an area — the reader chooses (SCRUM-1360).
 *
 * WHY IT IS A CHOICE. These are DAILY COUNTS: each point is a separate day's
 * total, not a reading of something continuous. Bars say that; a line implies
 * the value travelled between the points, which is why an area chart of six
 * scattered send-days reads as a trend that was never measured. Dr Shah asked
 * for bars, and bars are the honest default for this data — but the area view
 * stays available because a long, dense automation genuinely does read better
 * as a shape than as a hundred thin bars.
 *
 * The preference is remembered per browser, so the choice survives a reload
 * rather than resetting to the default on every visit. Keyed per surface so a
 * campaign and an automation can differ (see `storageKey`).
 *
 * Wording and control style deliberately mirror Reports' own chart picker
 * (ReportResultViewer's "Type:" select), because that is where the request came
 * from — "like FTP offers from reports".
 */
import { useEffect, useState } from 'react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

export type CampaignChartType = 'bar' | 'area' | 'line';

const CHART_TYPE_OPTIONS: Array<{ value: CampaignChartType; label: string }> = [
  { value: 'bar', label: 'Bar Chart' },
  { value: 'area', label: 'Area Chart' },
  { value: 'line', label: 'Line Chart' },
];

/** The two series, identical across both surfaces. */
const SERIES = [
  { key: 'opens', name: 'Opens', color: '#28a745' },
  { key: 'clicks', name: 'Clicks', color: '#ffc107' },
] as const;

export interface CampaignDailyChartProps {
  data: Array<{ date: string; opens: number; clicks: number }>;
  /**
   * Where the reader's choice is remembered. Distinct per surface so a
   * campaign and an automation keep their own.
   */
  storageKey: string;
  height?: number;
}

function readStored(key: string): CampaignChartType {
  try {
    const v = window.localStorage.getItem(key);
    if (v === 'bar' || v === 'area' || v === 'line') return v;
  } catch {
    // Private mode or storage disabled — the default is fine.
  }
  return 'bar';
}

export function CampaignDailyChart({ data, storageKey, height = 200 }: CampaignDailyChartProps) {
  const [chartType, setChartType] = useState<CampaignChartType>(() => readStored(storageKey));

  useEffect(() => {
    try { window.localStorage.setItem(storageKey, chartType); } catch { /* not worth failing over */ }
  }, [chartType, storageKey]);

  // Shared by all three chart types so switching changes only the marks, never
  // the axes, tooltip or legend — the reader is comparing the same numbers.
  const axes = (
    <>
      <CartesianGrid strokeDasharray="" stroke="#f0f0f0" vertical={false} />
      <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#999' }} axisLine={{ stroke: '#e5e5e5' }} tickLine={false} />
      <YAxis tick={{ fontSize: 11, fill: '#999' }} axisLine={false} tickLine={false} allowDecimals={false} domain={[0, 'auto']} />
      <Tooltip
        contentStyle={{ fontSize: 12, borderRadius: 6, border: '1px solid #e5e5e5', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
      />
      <Legend verticalAlign="bottom" iconType="circle" wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
    </>
  );

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 4 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#666' }}>
          Type:
          <select
            value={chartType}
            onChange={(e) => setChartType(e.target.value as CampaignChartType)}
            style={{
              fontSize: 12, padding: '2px 6px', borderRadius: 6,
              border: '1px solid #e5e5e5', background: '#fff', color: '#333',
            }}
          >
            {CHART_TYPE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </label>
      </div>

      <div style={{ height }}>
        <ResponsiveContainer width="100%" height="100%">
          {chartType === 'bar' ? (
            <BarChart data={data}>
              {axes}
              {SERIES.map((s) => (
                <Bar key={s.key} dataKey={s.key} name={s.name} fill={s.color} radius={[2, 2, 0, 0]} maxBarSize={28} />
              ))}
            </BarChart>
          ) : chartType === 'line' ? (
            <LineChart data={data}>
              {axes}
              {SERIES.map((s) => (
                <Line key={s.key} type="monotone" dataKey={s.key} name={s.name} stroke={s.color}
                  strokeWidth={2} dot={{ r: 2, fill: s.color }} activeDot={{ r: 5 }} />
              ))}
            </LineChart>
          ) : (
            <AreaChart data={data}>
              {axes}
              {SERIES.map((s) => (
                <Area key={s.key} type="monotone" dataKey={s.key} name={s.name} stroke={s.color}
                  fill={s.color === '#28a745' ? 'rgba(40, 167, 69, 0.1)' : 'rgba(255, 193, 7, 0.1)'}
                  strokeWidth={2} dot={{ r: 2, fill: s.color }} activeDot={{ r: 5 }} />
              ))}
            </AreaChart>
          )}
        </ResponsiveContainer>
      </div>
    </>
  );
}
